import { Children, isValidElement, cloneElement } from "react";
import { Info, Lightbulb, AlertTriangle, CheckCircle2, Square } from "lucide-react";

/* Presentational primitives for guide articles. On-brand: deep-blue + copper
   tokens, Poppins, generous spacing, lucide icons. All named exports. */

export const Lead = ({ children }) => (
  <p className="text-base leading-relaxed text-muted-foreground">{children}</p>
);

export const P = ({ children }) => (
  <p className="text-sm leading-relaxed text-foreground/90">{children}</p>
);

export const Section = ({ title, id, children }) => (
  <section className="space-y-3">
    {title && (
      <h2 id={id} className="scroll-mt-24 text-lg font-semibold tracking-tight text-foreground">{title}</h2>
    )}
    {children}
  </section>
);

export const Bullets = ({ children }) => (
  <ul className="space-y-1.5 pl-1">
    {Children.map(children, (c, i) => (
      <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-foreground/90">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
        <span>{c}</span>
      </li>
    ))}
  </ul>
);

export const Steps = ({ children }) => {
  const items = Children.toArray(children).filter(isValidElement);
  return (
    <ol className="space-y-3">
      {items.map((child, i) => cloneElement(child, { n: i + 1, key: i }))}
    </ol>
  );
};

export const Step = ({ n, title, children }) => (
  <li className="flex gap-3">
    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/12 text-xs font-semibold text-primary">
      {n}
    </span>
    <div className="space-y-1">
      {title && <p className="text-sm font-semibold text-foreground">{title}</p>}
      <div className="text-sm leading-relaxed text-foreground/90">{children}</div>
    </div>
  </li>
);

const CALLOUTS = {
  info: { icon: Info, ring: "border-primary/30", bg: "bg-primary/5", iconColor: "text-primary" },
  tip: { icon: Lightbulb, ring: "border-primary/40", bg: "bg-[rgba(201,119,46,0.07)]", iconColor: "text-primary" },
  warn: { icon: AlertTriangle, ring: "border-amber-300/60", bg: "bg-amber-50 dark:bg-amber-950/30", iconColor: "text-amber-600 dark:text-amber-400" },
  success: { icon: CheckCircle2, ring: "border-emerald-300/60", bg: "bg-emerald-50 dark:bg-emerald-950/30", iconColor: "text-emerald-600 dark:text-emerald-400" },
};

export const Callout = ({ type = "info", title, children }) => {
  const c = CALLOUTS[type] || CALLOUTS.info;
  const Icon = c.icon;
  return (
    <div className={`flex gap-3 rounded-xl border p-3.5 ${c.ring} ${c.bg}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${c.iconColor}`} />
      <div className="space-y-1 text-sm leading-relaxed text-foreground/90">
        {title && <p className="font-semibold text-foreground">{title}</p>}
        <div>{children}</div>
      </div>
    </div>
  );
};

/* Inline reference to a UI label (button / menu / field name). */
export const UI = ({ children }) => (
  <span className="whitespace-nowrap rounded-md bg-secondary px-1.5 py-0.5 text-[0.82em] font-medium text-foreground">{children}</span>
);

export const Checklist = ({ children }) => (
  <ul className="space-y-2">{children}</ul>
);

export const Check = ({ children }) => (
  <li className="flex items-start gap-2.5 text-sm leading-relaxed">
    <Square className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
    <span>{children}</span>
  </li>
);

export const CardGrid = ({ children }) => (
  <div className="grid gap-3 sm:grid-cols-2">{children}</div>
);

export const InfoCard = ({ icon: Icon, title, children }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="mb-1.5 flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-primary" />}
      <p className="text-sm font-semibold text-foreground">{title}</p>
    </div>
    <div className="text-sm leading-relaxed text-muted-foreground">{children}</div>
  </div>
);
